import {safeRound} from '../../helpers/logic'
import {PriceOption, SubscriptionData} from './types'

type PromoCycle = {price: number; toPay: boolean}

export type PassPromoPricing = {
  // False until the pass was re-fetched with ?firstCyclePrice= for the current first cycle.
  ready: boolean
  discount: number
  payToday: number
}

// Cycles go to the server at LIST price and the server applies the code (#2835): FIRST_PAYMENT to
// cycle 0 only, ALL_CYCLES to every cycle. This mirrors that to show the pay-today total, taking
// cycle 0's discount from the server (`firstCyclePromoDiscount`). Only an ALL_CYCLES pass with a
// second, part-period cycle due now (a start date in the past) falls back to the code's own rule.
export const getPassPromoPricing = ({
  cycles,
  plan,
  promo,
  listTotal,
}: {
  cycles: PromoCycle[]
  plan: PriceOption
  promo?: SubscriptionData['promoApplied']
  listTotal: number
}): PassPromoPricing | null => {
  if (!promo) {
    return null
  }
  const listPrice = plan.listPrice ?? plan.price
  const first = cycles[0] as PromoCycle | undefined
  if (!first) {
    return {ready: true, discount: 0, payToday: listTotal}
  }
  const ready =
    plan.firstCyclePrice !== undefined &&
    plan.firstCyclePromoDiscount !== undefined &&
    Math.abs(plan.firstCyclePrice - Math.min(first.price, listPrice)) < 0.01
  let discount = first.toPay ? plan.firstCyclePromoDiscount ?? 0 : 0
  if (promo.subscriptionDuration === 'ALL_CYCLES') {
    cycles.slice(1).forEach((cycle) => {
      if (!cycle.toPay || cycle.price <= 0) {
        return
      }
      if (cycle.price === listPrice && plan.promoDiscount !== undefined) {
        discount += plan.promoDiscount
        return
      }
      discount +=
        promo.discountType === 'PERCENT'
          ? safeRound((cycle.price * promo.discount) / 100)
          : Math.min(promo.discount, cycle.price)
    })
  }
  const rounded = safeRound(discount)
  return {ready, discount: rounded, payToday: safeRound(Math.max(0, listTotal - rounded))}
}
